import * as React from 'react';

export const useDebouncedValue = <T>(
  value: T,
  wait: number,
  maxWait: number = Number.MAX_VALUE,
) => {
  const [debouncedValue, setDebouncedValue] = React.useState(value);
  const lastCallTime = React.useRef(Date.now());
  const timeoutId = React.useRef<NodeJS.Timeout | null>(null);
  const maxTimeoutId = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    const now = Date.now();
    const timeSinceLastCall = now - lastCallTime.current;

    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }

    if (maxTimeoutId.current && timeSinceLastCall >= maxWait) {
      clearTimeout(maxTimeoutId.current);
      setDebouncedValue(value);
      lastCallTime.current = now;
    } else {
      timeoutId.current = setTimeout(() => {
        setDebouncedValue(value);
        lastCallTime.current = now;
      }, wait);

      if (!maxTimeoutId.current) {
        maxTimeoutId.current = setTimeout(() => {
          setDebouncedValue(value);
          lastCallTime.current = now;
          if (timeoutId.current) {
            clearTimeout(timeoutId.current);
          }
        }, maxWait - timeSinceLastCall);
      }
    }

    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
      if (maxTimeoutId.current) {
        clearTimeout(maxTimeoutId.current);
      }
    };
  }, [value, wait, maxWait]);

  return debouncedValue;
};
